<template>
  <b-container>
    <b-form>
      <b-row>
        <b-col>
          <ElementTextForm
            id="title"
            title="タイトル"
            :vModel="formdata.title"
            description="必須の項目です。"
            placeholder="成果のタイトル"
            :required="true"
            @changed="formChanged"
          ></ElementTextForm>

          <ElementTextArea
            id="detail"
            title="詳細"
            :rows="6"
            :vModel="formdata.detail"
            placeholder="成果の詳細（任意）"
            @changed="formChanged"
          ></ElementTextArea>

          <ElementTextForm
            id="link"
            title="関連リンク"
            placeholder="成果に関連するURL（任意）"
            :vModel="formdata.link"
            @changed="formChanged"
          ></ElementTextForm>

          <ElementSelectForm
            id="achievement_type_id"
            title="成果タイプ"
            :vModel="formdata.achievement_type_id"
            :options="achievementOptions"
            :required="true"
            @changed="formChanged"
          ></ElementSelectForm>

          <template v-if="isAuth && Number(formdata.achievement_type_id) === 8">
            <ElementSelectForm
              id="completion_program_time_id"
              title="修了年度"
              v-if="timeList.program_times && timeList.program_times.length"
              :vModel="formdata.completion_program_time_id"
              :options="getSelectList(timeList.program_times)"
              :required="true"
              @changed="formChanged"
            ></ElementSelectForm>
            <span v-else class="text-danger">
              修了年度の取得に失敗しました。
              <br />
              画面を更新してください。
            </span>
          </template>

          <ElementDatePicker title="日時" id="issued_at" :vModel="issued_at" @changed="formChanged"></ElementDatePicker>

          <ElementSelectForm
            id="is_draft"
            title="公開設定"
            :vModel="formdata.is_draft"
            :options="isDraftOptions"
            :required="true"
            @changed="formChanged"
          ></ElementSelectForm>

          <ElementFileForm
            :files="unuploadedFiles"
            :multiple="true"
            :needReset="true"
            :disabled="isUploading || isDisabled"
            :description="fileDescription"
            @fileAdded="fileAdded"
            id="files"
            title="添付ファイル"
          >
            <small tabindex="-1" class="form-text text-muted">鍵付きzipファイルはアップロードできません。</small>
            <template v-if="Object.keys(progress).length">
              <p class="mt-2">
                <strong>アップロード中のファイル</strong>
                <template v-if="isUploading">
                  <br />
                </template>
                <template v-for="val in Object.keys(progress)">
                  <span :key="'uploading' + val">
                    <br />
                    {{ progress[val].name }}
                    <span class="text-danger">
                      {{ progress[val].message }}
                      <b-link @click="deleteUploadingFile(val)">
                        <b-icon icon="x-circle-fill" variant="danger"></b-icon>
                      </b-link>
                    </span>
                    <b-progress :value="progress[val].progress" class="w-25" :variant="progress[val].status" />
                  </span>
                </template>
              </p>
            </template>
            <template v-if="filePaths.length">
              <p class="mt-2">
                <strong>新規登録ファイル</strong>
                <template v-for="(file, index) in filePaths">
                  <br :key="'tmpfile_' + index" />
                  {{ file.tmp_file_original_name }}
                  <b-link :key="index" @click="deleteTmpFile(index)">
                    <b-icon icon="trash-fill" variant="danger"></b-icon>
                  </b-link>
                </template>
              </p>
            </template>
            <p v-if="formdata.files.length > 0" class="m-1">
              <strong>登録済みファイル</strong>
              <template v-for="(uploadedFile, index) in uploadedFiles">
                <b-link :key="'uploaded-file_' + uploadedFile.id" @click.prevent.stop="downloadFile(uploadedFile.id)">
                  <br />
                  {{ uploadedFile.linkText }}
                </b-link>
                <span :key="'uploaded-file-text_' + index" class="text-muted small">{{ uploadedFile.text }}</span>
                <b-link :key="'uploaded-file-felete_' + index" @click="deleteUploadedFile(uploadedFile.id)">
                  <b-icon icon="trash-fill" variant="danger"></b-icon>
                </b-link>
              </template>
            </p>
          </ElementFileForm>
        </b-col>

        <b-col v-if="isAuth" lg="6">
          <UsersSelectForm
            title="成果登録先（必須）"
            @userSelected="userSelected"
            :selectedUserList="[Number(formdata.user_id)]"
            :isCheckbox="false"
          >
            <span v-if="!formdata.user_id" class="danger">
              登録先の選択は必須です。
              <br />
            </span>
          </UsersSelectForm>
          <input type="hidden" id="to_user_id" v-model="formdata.user_id" />
        </b-col>
      </b-row>

      <b-row>
        <b-col align="right">
          <b-button inline @click="closeModal(false)" class="mr-2">キャンセル</b-button>
          <b-button inline :disabled="isUploading" type="button" variant="default" @click="handleClickSave">
            保存
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-container>
</template>

<script>
import axios from 'axios'

import ElementFileForm from '@/components/presentationals/ElementFileForm.vue'
import ElementDatePicker from '@/components/presentationals/ElementDatePicker.vue'
import ElementTextForm from '@/components/presentationals/ElementTextForm.vue'
import ElementTextArea from '@/components/presentationals/ElementTextArea.vue'
import ElementSelectForm from '@/components/presentationals/ElementSelectForm.vue'
import UsersSelectForm from '@/components/UsersSelectForm.vue'
import { apiHeader } from '../api/httpClient'
import api from '@/api'
import baseJs from '@/transformer/base'
import { getArrangedDataSize } from '@/transformer/file'
import { createFileDownloadLink } from '@/transformer/achievement'
import { mapState } from 'vuex'

var fileIndex = 0
var deletedIndex = 0

export default {
  name: 'AchievementEdit',
  components: {
    ElementFileForm,
    ElementTextForm,
    ElementTextArea,
    ElementSelectForm,
    ElementDatePicker,
    UsersSelectForm
  },
  props: {
    data: Object,
    achievementOptions: Array
  },

  computed: {
    ...mapState({
      timeList: (state) => state.Achievement.timeList,
      fileRequiremants: (state) => state.Achievement.fileRequiremants
    }),
    isAuth() {
      return baseJs.isAuth()
    },
    issued_at() {
      if (this.data.issued_at) {
        return new Date(this.formdata.issued_at)
      }
      return null
    },
    uploadedFiles() {
      return this.formdata.files.map((file) => {
        return {
          linkText: file.file_name,
          id: file.id,
          text: '(' + getArrangedDataSize(file.file_size) + ')'
        }
      })
    },
    fileDescription() {
      if (this.remainedSpace < 1) {
        return 'サーバーに空きがないため、これ以上ファイルをアップロードできません'
      }
      if (this.remainedUploadableFileNum < 1) {
        return 'ひとつの成果に添付できるファイルは' + this.fileRequiremants.totalNum + 'つまでです'
      }
      let text = 'あと' + getArrangedDataSize(this.uploadableSingleFileSize) + '以下のファイルを' + this.remainedUploadableFileNum + 'つまで'
      if (this.remainedUploadableFileNum * this.fileRequiremants.singleSize > this.fileRequiremants.totalSize) {
        // 残容量が少ない時は、アップロードできるファイルサイズの残量を表示
        text += '、合計' + getArrangedDataSize(this.remainedSpace) + '以内で'
      }
      return text + '添付できます'
    },
    isDraftOptions() {
      return baseJs.getSelectionsByObject(this.$constList.stateList, false)
    },
    isDisabled() {
      if (this.remainedSpace < 1) { // サーバに空きがない
        return true
      }
      if (this.remainedUploadableFileNum < 1) { // アップロードできるファイル数を超えている
        return true
      }
      return false
    },
    remainedSpace() {
      // totalSizeは仮アップロードのたびに更新されるので、削除予定の容量とtotalSizeを足したのがアップロードできる容量
      return this.fileRequiremants.totalSize + this.deletedFileSize
    },
    remainedUploadableFileNum() {
      return this.fileRequiremants.totalNum - this.formdata.files.length - this.filePaths.length
    },

    uploadableSingleFileSize() {
      if (this.fileRequiremants.singleSize > this.remainedSpace) {
        // アップロードできるファイルサイズよりも空き容量が少ない
        return this.remainedSpace
      }
      return this.fileRequiremants.singleSize
    }
  },

  data() {
    return {
      progress: {},
      formdata: this.data,
      deletedFiles: [],
      unuploadedFiles: [],
      filePaths: [],
      selectedUserNames: [],
      isUploading: false,
      deletedFileSize: 0
    }
  },

  methods: {
    handleClickSave() {
      this.saveAchievement()
    },

    async saveAchievement() {
      let apiStatus = baseJs.getApiStatusEmpty()
      try {
        await api.achievement.save(this.formdata, this.filePaths, this.deletedFiles)
        apiStatus.customMessage = '成果「' + this.formdata.title + '」を'
        if (this.formdata.achievement_id) {
          apiStatus.customMessage += '編集しました。'
        } else {
          apiStatus.customMessage += '追加しました。'
        }
        this.$root.$bvToast.toast(apiStatus.customMessage, {
          variant: 'success',
          toaster: 'b-toaster-top-center',
          autoHideDelay: 3000
        })
        this.$emit('update')
        this.closeModal()
      } catch (err) {
        apiStatus = baseJs.getErrorStatus(err)
        const errorMessages = []
        for (const key in apiStatus.errors) {
          errorMessages.push(`・${apiStatus.errors[key]}`)
        }
        if (!errorMessages.length) {
          if (apiStatus.customMessage) {
            errorMessages.push(`・${apiStatus.customMessage}`)
          }
        }
        this.$root.$bvToast.toast(errorMessages.join('\n'), {
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          toastClass: 'pre-wrap',
          noAutoHide: true
        })
      }
    },
    closeModal(edited = true) {
      this.$emit('closeModal', edited)
    },
    deleteTmpFile(index) {
      this.filePaths.splice(index, 1)
      deletedIndex++
    },
    deleteUploadedFile(fileId) {
      this.deletedFiles.push({ id: fileId })
      const deletedFile = this.formdata.files.find((file) => file.id === fileId)
      this.deletedFileSize += Number(deletedFile.file_size)
      this.formdata.files = this.formdata.files.filter((e) => Number(e.id) !== Number(fileId))
    },
    deleteUploadingFile(index) {
      if (this.isUploading) {
        this.progress[index].cancel()
      } else {
        this.$delete(this.progress, index)
      }
    },
    downloadFile(fileId) {
      api.achievement
        .downloadFile(fileId)
        .then((res) => {
          const downloadLink = createFileDownloadLink(res)
          downloadLink.click()
        })
        .catch(() => {
          this.$root.$bvToast.toast(
            'ファイルのダウンロードに失敗しました。ファイルがすでに削除されている可能性があります。',
            {
              variant: 'danger',
              toaster: 'b-toaster-top-center',
              noAutoHide: true
            }
          )
        })
    },

    async uploadFile(file, index) {
      const CancelToken = axios.CancelToken

      const uploadKey = 'upload' + index
      this.progress[uploadKey] = { name: file.name, progress: 0, message: '', cancel: null, status: '' }

      const onUploadProgress = (e) => {
        const percent = Math.round((e.loaded * 100) / e.total)
        this.$set(this.progress[uploadKey], 'progress', percent)
        this.progress = { ...this.progress }
        this.isUploading = percent !== 100
      }

      const params = new FormData()
      params.append('tmp_file', file)

      const header = Object.assign(
        {
          onUploadProgress,
          cancelToken: new CancelToken((c) => {
            // An executor function receives a cancel function as a parameter
            this.progress[uploadKey].cancel = c
          })
        },
        apiHeader
      )
      axios
        .post(this.$constList.apiUrl + 'achievement/upload_tmp_file', params, header)
        .then((res) => {
          this.$set(this.progress[uploadKey], 'status', 'success')
          this.progress = { ...this.progress }
          this.filePaths.push(res.data.info)
          this.$delete(this.progress, uploadKey)
          this.$store.commit('Achievement/setTotalSize', res.data.remained_space)
        })
        .catch((error) => {
          this.$store.commit('Achievement/setTotalSize', error.response.data.remained_space)
          this.$set(this.progress[uploadKey], 'status', 'danger')
          if (error.message === 'canceled') {
            this.$set(this.progress[uploadKey], 'message', 'キャンセルしました。')
            this.isUploading = false
          } else if (error.response.data.remained_space < params.get('tmp_file').size) {
            this.$set(this.progress[uploadKey], 'message', 'アップロードできる容量を超えました。')
          } else {
            this.$set(this.progress[uploadKey], 'message', 'アップロードに失敗しました。')
          }
          deletedIndex++
          this.progress = { ...this.progress }
        })
    },
    async fileAdded(files) {
      let addedSize = 0
      await this.$nextTick().then((e) => {
        files.forEach((file) => {
          let errorMessage = ''

          // アップロードするファイル一つあたりのサイズをチェック
          if (file.size > this.uploadableSingleFileSize) {
            errorMessage = 'アップロードできるファイルのサイズは' + getArrangedDataSize(this.uploadableSingleFileSize) + 'までです'
          } else if (file.size > this.remainedSpace - addedSize) {
            errorMessage = 'アップロードできる総容量を超えています'
          } else if (fileIndex - deletedIndex + this.formdata.files.length >= this.fileRequiremants.totalNum) {
            errorMessage = '一つの成果に添付できるファイルは' + this.fileRequiremants.totalNum + 'つまでです'
          }
          if (errorMessage) {
            this.progress['uploadKey' + fileIndex] = { name: file.name, progress: 0, message: errorMessage, cancel: null, status: 'danger' }
            this.progress = { ...this.progress }
            deletedIndex++
          } else {
            addedSize += file.size
            this.uploadFile(file, fileIndex)
          }
          fileIndex++
        })
      })
    },
    formChanged(data) {
      this.formdata[data.id] = data.value
    },
    getSelectList(array) {
      let result = []
      if (array && array.length) {
        result = array.map((e) => {
          return { text: e.name, value: e.id }
        })
      }
      result.unshift({ text: 'なし', value: '0' })
      return result
    },
    userSelected(id) {
      // idは配列,
      this.formdata.user_id = id[0]
    }
  },
  mounted() {
    fileIndex = 0
    deletedIndex = 0
    this.deletedFileSize = 0
  }
}
</script>
